import type { FC } from 'react';

import type { TutorialSuggestionIllustration } from '../types';
import * as styles from './tutorial-renderer.css';

/** Docs, a journal entry and a table: the three kinds of written content. */
const CreateScene: FC = () => (
  <>
    <rect
      className={styles.illustrationNode}
      x="44"
      y="10"
      width="62"
      height="66"
      rx="5"
    />
    <path className={styles.illustrationEdge} d="M53 22h30" />
    <path
      className={styles.illustrationLine}
      d="M53 33h44M53 41h40M53 49h44M53 57h28"
    />
    <rect
      className={styles.illustrationNode}
      x="116"
      y="18"
      width="62"
      height="50"
      rx="5"
    />
    <path
      className={styles.illustrationTint}
      d="M121 18h52a5 5 0 0 1 5 5v7h-62v-7a5 5 0 0 1 5-5Z"
    />
    <path
      className={styles.illustrationLine}
      d="M116 30h62M116 42.5h62M116 55h62M137 30v38M157 30v38"
    />
    <circle className={styles.illustrationAccent} cx="126" cy="36.25" r="2.5" />
    <circle className={styles.illustrationAccent} cx="126" cy="48.75" r="2.5" />
  </>
);

/** Shapes, a sticky note and a pen stroke joined by connectors on a whiteboard. */
const VisualizeScene: FC = () => (
  <>
    <rect
      className={styles.illustrationNode}
      x="30"
      y="16"
      width="44"
      height="28"
      rx="5"
    />
    <circle className={styles.illustrationNode} cx="112" cy="58" r="15" />
    <rect
      className={styles.illustrationTint}
      x="146"
      y="12"
      width="40"
      height="36"
      rx="3"
    />
    <path className={styles.illustrationLine} d="M153 24h24M153 32h18" />
    <path className={styles.illustrationEdge} d="M74 30c18 0 24 12 26 18" />
    <path className={styles.illustrationEdge} d="m95 44 5 4.5 1.5-6.5" />
    <path className={styles.illustrationEdge} d="M127 55c10-2 16-6 19-12" />
    <path
      className={styles.illustrationLine}
      d="M34 62c6-8 10 6 16-2s9 6 15-1"
    />
  </>
);

/** A folder with nested pages, a tag and a favourite star. */
const StructureScene: FC = () => (
  <>
    <path
      className={styles.illustrationTint}
      d="M40 14h16l5 5h23a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H40a3 3 0 0 1-3-3V17a3 3 0 0 1 3-3Z"
    />
    <path className={styles.illustrationEdge} d="M50 37v31M50 49h14M50 68h14" />
    <rect
      className={styles.illustrationNode}
      x="66"
      y="42"
      width="54"
      height="14"
      rx="4"
    />
    <rect
      className={styles.illustrationNode}
      x="66"
      y="61"
      width="54"
      height="14"
      rx="4"
    />
    <path className={styles.illustrationLine} d="M73 49h30M73 68h24" />
    <path
      className={styles.illustrationNode}
      d="M136 20h30l10 10-10 10h-30a4 4 0 0 1-4-4V24a4 4 0 0 1 4-4Z"
    />
    <circle className={styles.illustrationAccent} cx="142" cy="30" r="3" />
    <path
      className={styles.illustrationAccent}
      d="m156 50 3.5 7.2 7.9 1.1-5.7 5.6 1.3 7.8-7-3.7-7 3.7 1.3-7.8-5.7-5.6 7.9-1.1Z"
    />
  </>
);

/** A trigger fanning out into steps, as in the workflow editor. */
const AutomateScene: FC = () => (
  <>
    <path
      className={styles.illustrationEdge}
      d="M62 43h24M114 43c12 0 12-18 26-18M114 43c12 0 12 18 26 18"
    />
    <circle className={styles.illustrationTint} cx="46" cy="43" r="16" />
    <path className={styles.illustrationAccent} d="M41 35.5v15l12-7.5Z" />
    <rect
      className={styles.illustrationNode}
      x="86"
      y="31"
      width="28"
      height="24"
      rx="6"
    />
    <path className={styles.illustrationLine} d="M93 40h14M93 47h9" />
    <rect
      className={styles.illustrationNode}
      x="140"
      y="15"
      width="40"
      height="20"
      rx="6"
    />
    <rect
      className={styles.illustrationNode}
      x="140"
      y="51"
      width="40"
      height="20"
      rx="6"
    />
    <path className={styles.illustrationLine} d="M148 25h22M148 61h16" />
    <circle className={styles.illustrationAccent} cx="86" cy="43" r="2.5" />
    <circle className={styles.illustrationAccent} cx="140" cy="25" r="2.5" />
    <circle className={styles.illustrationAccent} cx="140" cy="61" r="2.5" />
  </>
);

const SCENES: Record<TutorialSuggestionIllustration, FC> = {
  create: CreateScene,
  visualize: VisualizeScene,
  structure: StructureScene,
  automate: AutomateScene,
};

export const TutorialSuggestionIllustrationView: FC<{
  illustration: TutorialSuggestionIllustration;
}> = ({ illustration }) => {
  const Scene = SCENES[illustration];
  return (
    <span
      className={styles.suggestionPreview}
      data-illustration={illustration}
      aria-hidden="true"
    >
      <svg viewBox="0 0 216 86" focusable="false">
        <Scene />
      </svg>
    </span>
  );
};
